/**
 * THE BEAST READ MODEL — what a god who understands animals can be told
 * about one herd, off the truth the simulation actually keeps.
 *
 * ## What is here to read
 *
 * Wildlife is statistics on the social grid (`types.ts` header): a head count
 * per kind per 1,024 m cell, a K read daily from habitat, sere, season and
 * range wear, predation and carrion between the kinds, and a migration pass
 * that walks herds toward better headroom. No animal is an entity; no herd
 * is one either. So every reading below is a reading of ONE CELL'S POPULATION
 * OF ONE KIND and of the cells around it, and it says nothing about an
 * individual, a group under the population, a memory, or a signal — those
 * do not exist, and the Beasts nodes that would read them are marked
 * `absent` in `godhood/bindings.ts` in as many words.
 *
 * ## The one rule
 *
 * **Nothing here writes, and nothing here invents.** Every field is computed
 * from the ledger, the migration predicate (`inclinationOf`, the same
 * comparison the pass makes), the K decomposition (`capacityTerms`, the same
 * terms the daily step multiplies), the events ring (perception — the last
 * ~0.9 world-days at the shipped event rate; a reading off it says *lately*
 * and never *history*), and what the caller hands in about people. A reader
 * that could not find something true says nothing rather than something
 * plausible. `test/beasts.ts` advances a read world against an unread one and
 * asserts they are byte-identical.
 *
 * ## Why one seam
 *
 * `ui/subject.ts` and `main.ts`'s card must not query half the simulation to
 * describe a herd. This file answers the questions — what is this doing, who
 * is it with, where does its kind stand, is this movement unusual, what
 * pressures matter, what larger subject is this part of — as typed readings,
 * and `ui/beasts.ts` turns them into sentences. The Godhood Web decides WHICH
 * readings a god is shown (`main.ts` reads `hasRule`); this file computes them
 * all, cheaply, and does not know the Web exists.
 *
 * Pure by law 7: no three, no `Math.random`, no clock. `now` is passed in.
 */

import { DAY } from '@/sim/clock';
import type { EventRecord } from '@/sim/events';
import {
  SOCIAL_CELL_SIZE, socialCenter, socialKey, type SocialCellKey,
} from '@/sim/social/socialGrid';
import { BETTER, DECLINED, MIN_HERD, inclinationOf, type Inclination } from './migration';
import {
  MAX_PREDATION, PREDATOR_INTAKE, PREDATOR_SUPPORT, SCAV_PER_CARRION,
  type WildlifeSystem,
} from './populations';
import {
  HERBIVORES, WILDLIFE_ARCHETYPES,
  type CapacityTerms, type WildlifeCellState, type WildlifeSpeciesId,
} from './types';

/* ── what the caller hands in ─────────────────────────────────────────── */

export interface BeastContext {
  /** World-seconds now. */
  now: number;
  /**
   * How far out the readings survey, in rings of cells: 1 is the 3×3 around
   * the herd, 2 the 5×5, 3 the 7×7. The Godhood Web sets it (Known Range's
   * `beast.read.reach`); the default is the herd's own neighbours.
   */
  reach?: number;
  /** The events ring, or as much of it as the caller cares to pass. Perception, not memory. */
  events?: readonly EventRecord[];
  /**
   * People out hunting a cell right now — the sum of hunting-party sizes
   * standing on it — from whoever owns the bands. Optional; absent means the
   * mortal readings say nothing about people, never that there are none.
   */
  huntersAt?: (sx: number, sz: number) => number;
}

/* ── the readings ─────────────────────────────────────────────────────── */

/**
 * What the herd is doing about its ground — the dominant current state, and
 * only states the ledger can vouch for. Read as a herd, never as a mind.
 */
export type Drive =
  /** Fewer stand than the ground carries; the herd is growing, or being joined. */
  | 'gathering'
  /** Standing near what the ground carries. */
  | 'holding'
  /** More stand than the ground carries; dying back in place. */
  | 'thinning'
  /** The migration predicate is met: they walk today. */
  | 'leaving'
  /** A hunter with prey on its ground. */
  | 'hunting'
  /** A hunter whose ground no longer feeds it — prey thin, K under head. */
  | 'starving'
  /** No herd of this kind stands here. */
  | 'absent';

export interface DriveReading {
  drive: Drive;
  head: number;
  cap: number;
  /** Something is leaning on their drift, in the world's own units — signed, 0 for nothing. */
  pressed: number;
  /** World-second the kind died out here, when the ledger knows. */
  absentSince?: number;
  inclination: Inclination;
}

/** What an animal keeps company with, and whether its kind holds the ground around it. */
export interface CompanyReading {
  head: number;
  /** Of the eight neighbouring cells, how many hold a herd (≥ MIN_HERD) of the kind. */
  neighboursHolding: number;
  neighbours: number;
}

/** Where this kind stands around here, and whether this ground is the middle or the edge of it. */
export interface RangeReading {
  reach: number;
  /** Cells in the survey (this one excluded) that exist on the map. */
  surveyed: number;
  /** Of them, how many hold a herd of the kind. */
  holding: number;
  /** 'middle' — every neighbour holds it; 'edge' — some do; 'beyond' — none do. */
  standing: 'middle' | 'edge' | 'beyond';
  /** Cells in the survey the kind was on within the year and is gone from. */
  latelyLost: number;
  /** Total head of the kind across the survey, this cell included. */
  countryHead: number;
}

/** Recent movement of this kind at this ground, off the events ring. */
export interface MovementReading {
  /** How far back the ring reached, in world-days — honesty about "lately". */
  daysSeen: number;
  /** Arrivals onto this cell: heads and the compass direction they came FROM. */
  arrived: number;
  arrivedFrom: Compass | null;
  /** Departures off this cell: heads and where they went. */
  left: number;
  leftFor: Compass | null;
  /** Any of the departures was a flight from failing ground rather than a drift after better. */
  fled: boolean;
}

export type Compass = 'north' | 'south' | 'east' | 'west'
  | 'north-east' | 'north-west' | 'south-east' | 'south-west';

/** This herd against the others of its kind around it. */
export interface CompareReading {
  /** How many neighbouring herds of the kind there were to compare with. */
  against: number;
  /** This herd's head over the neighbours' mean head; 1 is alike. */
  density: number;
  /** This herd's trend against the neighbours': 'better' — growing while they decline, etc. */
  trend: 'better' | 'worse' | 'alike';
  /** This ground's range against the neighbours' mean range. */
  wear: 'worse' | 'better' | 'alike';
}

/** What the season is doing to the ground under a herd. */
export interface SeasonReading {
  /** The cure the forage takes right now, 0..DRY_CUT (0.5). */
  cure: number;
  water: number;
  /** Does the season currently matter here at all? */
  curing: boolean;
  /** Is this ground the kind the herds keep to when it does — by the water? */
  refuge: boolean;
}

/** The scavengers' side of what is dying here. */
export interface CarrionReading {
  carrion: number;
  scavengers: number;
  scavengerCap: number;
  /** Something is dying on this ground — enough carrion to matter. */
  dying: boolean;
  /** The scavengers' K is above their head: they are gathering to it. */
  gathering: boolean;
}

/** Predator against prey, read from whichever side the herd is on. */
export interface PreyReading {
  /** For a hunter: prey head on its ground and the K that prey supports. */
  prey: number;
  support: number;
  /** For a hunter: hungrier than the ground can feed. */
  hungry: boolean;
  /** For a hunter: which prey kind carries it most, if any. */
  mainPrey: WildlifeSpeciesId | null;
  /** For prey: hunters standing on this ground. */
  hunters: number;
  /** For prey: the daily share of the herd the hunters take, 0..MAX_PREDATION. */
  takeShare: number;
  /** For prey: hunted hard, hunted, or little hunted. */
  hunted: 'hard' | 'some' | 'little' | 'none';
}

/** People, seen from the animals' side. */
export interface MortalReading {
  /** People hunting this cell right now, when the caller could say. */
  huntersHere?: number;
  /** …and on the neighbouring cells. */
  huntersNear?: number;
  /** predator_incident events near this ground within `daysSeen`. */
  incidents: number;
  daysSeen: number;
}

/**
 * THE LOCAL POPULATION — the connected ground around a cell that holds a
 * herd of the kind, bounded to a small country. This is the larger subject
 * a selection resolves into past the herd's own footprint: not an entity,
 * not stored, a reading of the ledger the way `stirring()` is (law 9). Two
 * herds a valley apart with no herd between them are two populations, which
 * is the truth of the ledger and nothing more.
 */
export interface PopulationReading {
  species: WildlifeSpeciesId;
  /** Cells in the component, this one first, then by distance and key. */
  cells: Array<{ sx: number; sz: number }>;
  head: number;
  cap: number;
  /** Cells whose herd is growing / declining (cellView's own trend cut). */
  growing: number;
  declining: number;
  /** Cells whose range is worn under 0.8. */
  worn: number;
  /** Cells with people hunting them, when the caller could say. */
  hunted?: number;
  /** Cells that also carry a hunter of the kind's predators. */
  sharedWithHunters: number;
  /** Ground bordering the component the kind was on within the year and is gone from. */
  latelyLost: number;
  /** Ground the kind came back onto within the ring's memory — `wildlife_returned` nearby. */
  returned: number;
  /** Cells whose herd walks today. */
  leaving: number;
  /** The component ran into the survey's bound; the population is larger than said. */
  truncated: boolean;
}

/**
 * A REAL BRANCH — the herd is close to a threshold the simulation actually
 * has, and which way it goes is not decided. Says that something is undecided
 * here; never which way.
 */
export interface BranchReading {
  /** Inclined to move and short of the bar: the stay/leave branch is live. */
  nearLeaving: boolean;
  /** Today's K has fallen toward the DECLINED trigger without reaching it: the ground is failing under them. */
  groundFailing: boolean;
  /** Standing well over K on worn range: thrive/fail. */
  overstretched: boolean;
}

/** WHY the ground carries what it does — the K decomposition, phrased later. */
export interface RoleReading {
  terms: CapacityTerms;
  /** The sere stage carrying most of the cell, if any burn is still remembered. */
  stage: 'bare' | 'grass' | 'shrub' | 'young' | null;
  stageShare: number;
  /** For a hunter: the prey kind carrying most of its K. */
  mainPrey: WildlifeSpeciesId | null;
  /** For a scavenger: carried more by carrion than by unrest. */
  carrionCarried: boolean;
  /** Hunters standing on the same ground — the next link of the chain. */
  huntersHere: number;
}

/* ── the whole reading, in one call ───────────────────────────────────── */

export interface BeastReading {
  species: WildlifeSpeciesId;
  sx: number;
  sz: number;
  drive: DriveReading;
  company: CompanyReading;
  range: RangeReading;
  movement: MovementReading;
  compare: CompareReading;
  season: SeasonReading | null;
  carrion: CarrionReading;
  prey: PreyReading;
  mortal: MortalReading;
  branch: BranchReading;
  role: RoleReading | null;
}

/** How far back "lately" reaches, in world-days. The ring holds ~0.9 days at the shipped event rate; a longer bound is honest about what it could NOT see. */
export const LATELY_DAYS = 30;

/** Range wear under this reads as worn — `ui/thread.ts`'s own cut. */
const WORN = 0.8;

/* ── the individual readers ───────────────────────────────────────────── */

const cellAt = (w: WildlifeSystem, sx: number, sz: number) => w.cellRead(sx, sz);

/** The strongest lean on a herd across its processes, signed; 0 for none. */
const strongestPress = (by?: Partial<Record<'movement' | 'increase' | 'hunt', { v: number }>>): number => {
  if (!by) return 0;
  let best = 0;
  for (const k of ['movement', 'increase', 'hunt'] as const) {
    const v = by[k]?.v ?? 0;
    if (Math.abs(v) > Math.abs(best)) best = v;
  }
  return best;
};

export function driveOf(w: WildlifeSystem, sx: number, sz: number, s: WildlifeSpeciesId): DriveReading {
  const c = cellAt(w, sx, sz);
  const cells = w.cellsRead() as ReadonlyMap<SocialCellKey, WildlifeCellState>;
  if (!c) {
    return { drive: 'absent', head: 0, cap: 0, pressed: 0,
             inclination: { here: 1, best: null, bestR: 1, declined: false, gradient: 0,
                            pressed: 0, bar: BETTER, would: false } };
  }
  const head = c.pop[s], cap = c.cap[s];
  const inclination = inclinationOf(cells, c as WildlifeCellState, s);
  const pressed = strongestPress(c.press?.[s]);
  const since = c.gone[s];
  const base = { head, cap, pressed, inclination, ...(since !== undefined ? { absentSince: since } : {}) };
  if (head < 1) return { drive: 'absent', ...base };
  const a = WILDLIFE_ARCHETYPES[s];
  if (a.prey.length) {
    let prey = 0;
    for (const p of a.prey) prey += c.pop[p];
    // a hunter is starving when its ground no longer feeds it: K (prey / 30,
    // or carrion for the scavengers) has fallen under its head
    if (head > cap * 1.1 || (s === 'predator' && prey <= 0)) return { drive: 'starving', ...base };
    return { drive: 'hunting', ...base };
  }
  if (inclination.would) return { drive: 'leaving', ...base };
  if (head > cap * 1.1) return { drive: 'thinning', ...base };
  if (head < cap * 0.9 && cap > 0.5) return { drive: 'gathering', ...base };
  return { drive: 'holding', ...base };
}

export function companyOf(w: WildlifeSystem, sx: number, sz: number, s: WildlifeSpeciesId): CompanyReading {
  const c = cellAt(w, sx, sz);
  let neighbours = 0, holding = 0;
  for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
    if (!dx && !dz) continue;
    const n = cellAt(w, sx + dx, sz + dz);
    if (!n) continue;
    neighbours++;
    if (n.pop[s] >= MIN_HERD) holding++;
  }
  return { head: c?.pop[s] ?? 0, neighboursHolding: holding, neighbours };
}

export function rangeOf(
  w: WildlifeSystem, sx: number, sz: number, s: WildlifeSpeciesId, now: number, reach = 1,
): RangeReading {
  const r = Math.max(1, Math.min(4, Math.round(reach)));
  let surveyed = 0, holding = 0, lost = 0, countryHead = 0, ring1 = 0, ring1Holding = 0;
  for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) {
    const n = cellAt(w, sx + dx, sz + dz);
    if (!n) continue;
    if (!dx && !dz) { countryHead += n.pop[s]; continue; }
    surveyed++;
    countryHead += n.pop[s];
    const holds = n.pop[s] >= MIN_HERD;
    if (holds) holding++;
    if (Math.max(Math.abs(dx), Math.abs(dz)) === 1) { ring1++; if (holds) ring1Holding++; }
    const gone = n.gone[s];
    if (gone !== undefined && now - gone <= 365 * DAY) lost++;
  }
  const standing = ring1 > 0 && ring1Holding === ring1 ? 'middle' : ring1Holding > 0 ? 'edge' : 'beyond';
  return { reach: r, surveyed, holding, standing, latelyLost: lost, countryHead };
}

/** Compass word for a step from (fx,fz) to (tx,tz) in cell coordinates. */
export const compassOf = (dx: number, dz: number): Compass | null => {
  if (!dx && !dz) return null;
  const ns = dz < 0 ? 'north' : dz > 0 ? 'south' : '';
  const ew = dx > 0 ? 'east' : dx < 0 ? 'west' : '';
  return (ns && ew ? `${ns}-${ew}` : ns || ew) as Compass;
};

export function movementOf(
  sx: number, sz: number, s: WildlifeSpeciesId, now: number, events: readonly EventRecord[] = [],
): MovementReading {
  const centre = socialCenter(sx, sz);
  let arrived = 0, left = 0, fled = false;
  let arrivedFrom: Compass | null = null, leftFor: Compass | null = null;
  let oldest = now;
  // strongest move decides the direction word, so one big flight is not
  // outvoted by three stragglers the other way
  let bestIn = 0, bestOut = 0;
  for (const e of events) {
    if (e.type !== 'wildlife_migrated' || e.data?.species !== s) continue;
    if (now - e.time > LATELY_DAYS * DAY) continue;
    oldest = Math.min(oldest, e.time);
    const moved = typeof e.data?.moved === 'number' ? e.data.moved : 0;
    const toSx = typeof e.data?.toSx === 'number' ? e.data.toSx : NaN;
    const toSz = typeof e.data?.toSz === 'number' ? e.data.toSz : NaN;
    // the event's (x, z) is the ORIGIN cell's centre
    const fromHere = Math.abs(e.x - centre.x) < SOCIAL_CELL_SIZE / 2 && Math.abs(e.z - centre.z) < SOCIAL_CELL_SIZE / 2;
    const toHere = toSx === sx && toSz === sz;
    if (fromHere && Number.isFinite(toSx)) {
      left += moved;
      if (e.data?.fled === true) fled = true;
      if (moved > bestOut) { bestOut = moved; leftFor = compassOf(toSx - sx, toSz - sz); }
    } else if (toHere) {
      arrived += moved;
      const fsx = Math.floor(e.x / SOCIAL_CELL_SIZE), fsz = Math.floor(e.z / SOCIAL_CELL_SIZE);
      if (moved > bestIn) { bestIn = moved; arrivedFrom = compassOf(fsx - sx, fsz - sz); }
    }
  }
  return { daysSeen: (now - oldest) / DAY, arrived, arrivedFrom, left, leftFor, fled };
}

export function compareOf(w: WildlifeSystem, sx: number, sz: number, s: WildlifeSpeciesId): CompareReading {
  const c = cellAt(w, sx, sz);
  if (!c) return { against: 0, density: 1, trend: 'alike', wear: 'alike' };
  const trendOf = (x: Readonly<WildlifeCellState>) => {
    const v = w.cellView(x.sx, x.sz, s);
    return v?.trend ?? 'steady';
  };
  let n = 0, head = 0, range = 0, growing = 0, declining = 0;
  for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
    if (!dx && !dz) continue;
    const x = cellAt(w, sx + dx, sz + dz);
    if (!x || x.pop[s] < MIN_HERD) continue;
    n++; head += x.pop[s]; range += x.range;
    const t = trendOf(x);
    if (t === 'growing') growing++; else if (t === 'declining') declining++;
  }
  if (!n) return { against: 0, density: 1, trend: 'alike', wear: 'alike' };
  const density = c.pop[s] / (head / n);
  const mine = trendOf(c);
  const theirs = growing > declining ? 'growing' : declining > growing ? 'declining' : 'steady';
  const rank = (t: string) => (t === 'growing' ? 1 : t === 'declining' ? -1 : 0);
  const trend = rank(mine) > rank(theirs) ? 'better' : rank(mine) < rank(theirs) ? 'worse' : 'alike';
  const meanRange = range / n;
  const wear = c.range < meanRange - 0.1 ? 'worse' : c.range > meanRange + 0.1 ? 'better' : 'alike';
  return { against: n, density, trend, wear };
}

export function seasonOf(terms: CapacityTerms | null): SeasonReading | null {
  if (!terms) return null;
  return {
    cure: terms.cure, water: terms.water,
    curing: terms.cure >= 0.12,
    refuge: terms.water >= 0.5,
  };
}

export function carrionOf(w: WildlifeSystem, sx: number, sz: number): CarrionReading {
  const c = cellAt(w, sx, sz);
  if (!c) return { carrion: 0, scavengers: 0, scavengerCap: 0, dying: false, gathering: false };
  const cap = c.cap.scavenger, pop = c.pop.scavenger;
  return {
    carrion: c.carrion, scavengers: pop, scavengerCap: cap,
    // one head-unit of carrion feeds two and a half scavengers; under that
    // it is the ordinary attrition of a living ground
    dying: c.carrion * SCAV_PER_CARRION >= 2.5,
    gathering: cap > pop * 1.1 && cap >= MIN_HERD,
  };
}

export function preyOf(w: WildlifeSystem, sx: number, sz: number, s: WildlifeSpeciesId): PreyReading {
  const c = cellAt(w, sx, sz);
  const none: PreyReading = { prey: 0, support: 0, hungry: false, mainPrey: null,
                              hunters: 0, takeShare: 0, hunted: 'none' };
  if (!c) return none;
  const a = WILDLIFE_ARCHETYPES[s];
  if (a.prey.length) {
    let prey = 0, main: WildlifeSpeciesId | null = null, most = 0;
    for (const p of a.prey) {
      prey += c.pop[p];
      if (c.pop[p] > most) { most = c.pop[p]; main = p; }
    }
    const support = s === 'predator' ? prey / PREDATOR_SUPPORT : c.cap[s];
    return { ...none, prey, support, mainPrey: main, hungry: c.pop[s] > support * 1.1 };
  }
  // prey: what hunts them and how hard. The daily take a hunter makes on this
  // kind, as a share of the kind — exactly `daily`'s predation arithmetic.
  const hunters = c.pop.predator;
  const preyHere = WILDLIFE_ARCHETYPES.predator.prey.reduce((t, p) => t + c.pop[p], 0);
  const takeShare = hunters > 0 && preyHere > 0
    ? Math.min(hunters * PREDATOR_INTAKE / preyHere, MAX_PREDATION) : 0;
  const hunted = takeShare >= 0.05 ? 'hard' : takeShare >= 0.02 ? 'some' : hunters > 0 ? 'little' : 'none';
  return { ...none, hunters, takeShare, hunted };
}

export function mortalOf(
  sx: number, sz: number, now: number, ctx: BeastContext,
): MortalReading {
  const out: MortalReading = { incidents: 0, daysSeen: 0 };
  if (ctx.huntersAt) {
    out.huntersHere = ctx.huntersAt(sx, sz);
    let near = 0;
    for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
      if (!dx && !dz) continue;
      near += ctx.huntersAt(sx + dx, sz + dz);
    }
    out.huntersNear = near;
  }
  const centre = socialCenter(sx, sz);
  let oldest = now;
  for (const e of ctx.events ?? []) {
    if (e.type !== 'predator_incident') continue;
    if (now - e.time > LATELY_DAYS * DAY) continue;
    if (Math.abs(e.x - centre.x) > SOCIAL_CELL_SIZE || Math.abs(e.z - centre.z) > SOCIAL_CELL_SIZE) continue;
    out.incidents++;
    oldest = Math.min(oldest, e.time);
  }
  out.daysSeen = (now - oldest) / DAY;
  return out;
}

/** The furthest the population survey walks from the herd, in cells. */
export const POPULATION_REACH = 3;
/** …and the most cells it will count before saying "larger than this". */
export const POPULATION_CELLS = 40;

export function populationOf(
  w: WildlifeSystem, sx: number, sz: number, s: WildlifeSpeciesId, ctx: BeastContext,
): PopulationReading {
  const cells = w.cellsRead() as ReadonlyMap<SocialCellKey, WildlifeCellState>;
  const start = cells.get(socialKey(sx, sz));
  const out: PopulationReading = {
    species: s, cells: [], head: 0, cap: 0, growing: 0, declining: 0, worn: 0,
    ...(ctx.huntersAt ? { hunted: 0 } : {}),
    sharedWithHunters: 0, latelyLost: 0, returned: 0, leaving: 0, truncated: false,
  };
  if (!start || start.pop[s] < MIN_HERD) return out;

  // breadth-first over cells holding a herd of the kind, Chebyshev-bounded.
  // The frontier is walked in a fixed order (dz-major, dx-minor) and the
  // component is a set of keys, so the result is a function of the ledger.
  const seen = new Set<SocialCellKey>([socialKey(sx, sz)]);
  const queue: WildlifeCellState[] = [start];
  const border = new Set<SocialCellKey>();
  while (queue.length) {
    const c = queue.shift()!;
    if (out.cells.length >= POPULATION_CELLS) { out.truncated = true; break; }
    out.cells.push({ sx: c.sx, sz: c.sz });
    out.head += c.pop[s];
    out.cap += c.cap[s];
    const v = w.cellView(c.sx, c.sz, s);
    if (v?.trend === 'growing') out.growing++;
    else if (v?.trend === 'declining') out.declining++;
    if (c.range < WORN) out.worn++;
    if (ctx.huntersAt && ctx.huntersAt(c.sx, c.sz) > 0) out.hunted = (out.hunted ?? 0) + 1;
    if (!WILDLIFE_ARCHETYPES[s].prey.length && c.pop.predator >= 1) out.sharedWithHunters++;
    if (inclinationOf(cells, c, s).would) out.leaving++;
    for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
      if (!dx && !dz) continue;
      const nx = c.sx + dx, nz = c.sz + dz;
      const key = socialKey(nx, nz);
      if (seen.has(key)) continue;
      const n = cells.get(key);
      if (!n) continue;
      if (Math.max(Math.abs(nx - sx), Math.abs(nz - sz)) > POPULATION_REACH) {
        // holding ground past the survey's bound: the population is larger than said
        if (n.pop[s] >= MIN_HERD) out.truncated = true;
        continue;
      }
      if (n.pop[s] >= MIN_HERD) { seen.add(key); queue.push(n); }
      else border.add(key);
    }
  }
  // the ground around the component that the kind was on lately and is gone from
  for (const key of border) {
    const n = cells.get(key)!;
    const gone = n.gone[s];
    if (gone !== undefined && ctx.now - gone <= 365 * DAY) out.latelyLost++;
  }
  // …and ground it came back onto, as far as the ring remembers
  for (const e of ctx.events ?? []) {
    if (e.type !== 'wildlife_returned' || e.data?.species !== s) continue;
    if (ctx.now - e.time > LATELY_DAYS * DAY) continue;
    const ex = Math.floor(e.x / SOCIAL_CELL_SIZE), ez = Math.floor(e.z / SOCIAL_CELL_SIZE);
    if (Math.max(Math.abs(ex - sx), Math.abs(ez - sz)) <= POPULATION_REACH + 1) out.returned++;
  }
  return out;
}

export function branchOf(drive: DriveReading, terms: CapacityTerms | null): BranchReading {
  const inc = drive.inclination;
  return {
    nearLeaving: !inc.would && inc.gradient >= 0.4 && drive.head >= MIN_HERD,
    groundFailing: !!terms && terms.prevCap > 0.5 && terms.cap < terms.prevCap * 0.85
      && !(terms.cap < terms.prevCap * DECLINED),
    overstretched: drive.cap > 0 && drive.head > drive.cap * 1.25 && (terms?.range ?? 1) < WORN,
  };
}

export function roleOf(terms: CapacityTerms | null, s: WildlifeSpeciesId, cell?: Readonly<WildlifeCellState>): RoleReading | null {
  if (!terms) return null;
  const stages = ['bare', 'grass', 'shrub', 'young'] as const;
  let stage: RoleReading['stage'] = null, stageShare = 0;
  for (const st of stages) if (terms.shares[st] > stageShare) { stageShare = terms.shares[st]; stage = st; }
  if (stageShare < 0.25) { stage = null; stageShare = 0; }
  let mainPrey: WildlifeSpeciesId | null = null;
  if (cell && WILDLIFE_ARCHETYPES[s].prey.length) {
    let most = 0;
    for (const p of WILDLIFE_ARCHETYPES[s].prey) if (cell.pop[p] > most) { most = cell.pop[p]; mainPrey = p; }
  }
  const carrionCarried = s === 'scavenger' && terms.carrion * SCAV_PER_CARRION > terms.instability * 15;
  return { terms, stage, stageShare, mainPrey, carrionCarried, huntersHere: cell?.pop.predator ?? 0 };
}

/* ── all of it ────────────────────────────────────────────────────────── */

/**
 * Every reading for one herd, computed once. A card is drawn for one subject
 * at a time and the surveys are a few dozen `Map.get`s, so nothing here is
 * worth caching; what matters is that it is ONE call, so the interface has
 * one seam into the wild and not nine.
 */
export function readBeast(
  w: WildlifeSystem, sx: number, sz: number, s: WildlifeSpeciesId, ctx: BeastContext,
): BeastReading {
  const cell = w.cellRead(sx, sz);
  const terms = w.capacityTerms(sx, sz, s, ctx.now);
  const drive = driveOf(w, sx, sz, s);
  return {
    species: s, sx, sz,
    drive,
    company: companyOf(w, sx, sz, s),
    range: rangeOf(w, sx, sz, s, ctx.now, ctx.reach ?? 1),
    movement: movementOf(sx, sz, s, ctx.now, ctx.events),
    compare: compareOf(w, sx, sz, s),
    season: HERBIVORES.includes(s) ? seasonOf(terms) : null,
    carrion: carrionOf(w, sx, sz),
    prey: preyOf(w, sx, sz, s),
    mortal: mortalOf(sx, sz, ctx.now, ctx),
    branch: branchOf(drive, terms),
    role: roleOf(terms, s, cell),
  };
}
